import React from "react";
import { observer } from "mobx-react-lite";
import { FiltersStore } from "./FiltersStore";
import {
  IconCapsuleHorizontal,
  IconDownload,
  IconFileCertificate,
  IconHexagon,
  IconLayoutRows,
} from "@tabler/icons-react";
import { useParams } from "react-router";
import ButtonGroups from "components/ButtonGroups";
import clsx from "clsx";
import AdvancedFilterView from "./ViewLayouts/AdvancedFilterView";
import SimpleFilterView from "./ViewLayouts/SimpleFilterView";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";
import useDynamicTable from "features/DynamicTable/useDynamicTable";
import useToast from "../../../../hooks/useToast.ts";
import { t } from "i18next";
import { inventoryStore } from "../../../Inventory/InventoryStore.tsx";
import LoadingIcon from "../../../../components/LoadingIcon";
import { useFeatureAccess } from "../../../../hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";

export const FILTER_VIEW_OPTIONS = [
  { id: "simple", icon: <IconCapsuleHorizontal size={18} stroke={3} /> },
  { id: "advanced", icon: <IconLayoutRows size={18} stroke={3} /> },
];

export enum FilterDefaultGrouping {
  EPD = "EPD",
  Object = "Object",
}

export const DEFAULT_GROUPING = [
  {
    id: FilterDefaultGrouping.Object,
    name: "Object",
    icon: <IconHexagon size={18} stroke={2} />,
  },
  {
    id: FilterDefaultGrouping.EPD,
    name: "EPD",
    icon: <IconFileCertificate size={18} stroke={2} />,
  },
];
const FiltersBar = (props: {
  isInventory?: boolean;
  filterStore: FiltersStore;
}) => {
  const { ifc_id } = useParams();
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [reportIdToWait, setReportIdToWait] = React.useState<string | null>(
    null
  );
  const filtersStore = props.filterStore;

  const toast = useToast();
  const { hasFeature } = useFeatureAccess();

  const {
    retrieveFiltersState,
    saveFilterRules,
    saveSortRules,
    retrieveSortState,
    retrieveGroupingState,
    saveGroupingRules,
    saveColumnsOrder,
    saveColumnsVisibilityState,
    refreshTable,
  } = useDynamicTable();

  React.useEffect(() => {
    dynamicTableStore.properties.length === 0 &&
      dynamicTableStore.getProperties();
  }, []);

  React.useEffect(() => {
    retrieveSortState();
    retrieveFiltersState();
    retrieveGroupingState();
  }, [ifc_id]);

  React.useEffect(() => {
    saveColumnsOrder();
    saveColumnsVisibilityState();
    (async () => await refreshTable())();
    setFirstLoad(false);
  }, [dynamicTableStore.visibleColumnProperties]);

  React.useEffect(() => {
    retrieveGroupingState();
    retrieveFiltersState();
  }, [dynamicTableStore.properties]);

  React.useEffect(() => {
    if (firstLoad) return;
    refreshTable();
    saveFilterRules();
  }, [filtersStore.validFilterRules]);

  React.useEffect(() => {
    if (firstLoad) return;
    refreshTable();
    saveSortRules();
  }, [filtersStore.validSortingRules]);

  React.useEffect(() => {
    saveGroupingRules();
  }, [filtersStore.validGroupByRules.length]);

  React.useEffect(() => {
    if (firstLoad) return;
    refreshTable();
  }, [filtersStore.defaultGrouping]);

  async function triggerExport() {
    const response = await dynamicTableStore.triggerExport();
    if (response && response.export_id) {
      setReportIdToWait(response.export_id);
      const report = await inventoryStore.checkReport(response.export_id);
      if (report && report.download_url) {
        window.open(report.download_url);
      } else {
        // it took longer than 10 secs
        toast(t("buildingInventory.exportTrigger.success"), "success");
      }
    } else {
      toast(t("buildingInventory.exportTrigger.failure"), "warning");
    }
    setReportIdToWait(null);
  }

  return (
    <div
      className={clsx(
        "w-full px-4 flex justify-between text-xs font-medium items-center",
        { "h-[42px]": filtersStore.selectedFilterView?.id === "simple" }
      )}
    >
      {filtersStore.selectedFilterView?.id === "simple" ? (
        <SimpleFilterView />
      ) : (
        <AdvancedFilterView />
      )}

      <div className="flex gap-4 items-center">
        <ButtonGroups
          optionClsssName="!max-h-[26px] px-2 py-1 !rounded-full border-none"
          containerClassName="border border-gray-300 rounded-full"
          groupClassName="!mt-0"
          onChange={(value) => filtersStore.setSelectedFilterView(value)}
          items={FILTER_VIEW_OPTIONS}
          current={FILTER_VIEW_OPTIONS[0]}
        ></ButtonGroups>

        {props.isInventory && (
          <>
            <ButtonGroups
              optionClsssName="!h-[36px] px-2 py-1 !rounded-full border-none"
              containerClassName="border border-gray-300 rounded-full"
              groupClassName="!mt-0"
              onChange={(value) =>
                value?.id &&
                filtersStore.setdefaultGrouping(
                  value.id as FilterDefaultGrouping
                )
              }
              items={DEFAULT_GROUPING}
              current={DEFAULT_GROUPING.find(
                (e) => e.id == filtersStore.defaultGrouping
              )}
            ></ButtonGroups>
            {hasFeature(LicenseFeatureEnum.InventoryExports) &&
              (reportIdToWait ? (
                <LoadingIcon type="secondary" className="m-auto !w-8 !h-8" />
              ) : (
                <IconDownload
                  size={24}
                  stroke={2}
                  onClick={() => triggerExport()}
                  className="cursor-pointer hover:text-gray-700 transition"
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default observer(FiltersBar);
